import React, { useState, useEffect, useRef, useMemo } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import axios from 'axios';
import { ShoppingCart, Heart, User, Store, LogOut, Search, Star, Play, Globe, Package, MessageSquare, Download, Menu, X, ChevronRight, Shield, Award, TrendingUp, ExternalLink, BadgeCheck, CheckCircle, DollarSign, Flame, Eye, BookOpen, ArrowRight, Users, Zap, Trophy, Sparkles } from 'lucide-react';
import Logo from '@/components/Logo';
import { trackStorefrontClick, trackProductClick } from '../utils/trackClick';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { toast } from 'sonner';
import AuthModal from '@/components/AuthModal';
import { useTranslation } from 'react-i18next';
import SEOHead, { generateWebsiteSchema, generateOrganizationSchema, generateMarketplaceSchema, generateFAQSchema } from '@/components/SEOHead';
import { getProductImage } from '@/utils/imageUtils';
import ResilientImg from '@/components/ResilientImg';
import ScoutDiscover from '@/components/ScoutDiscover';
import { getCart as getStoredCart, setCart as saveCart, clearCart as clearStoredCart } from '@/utils/cartStorage';
import PublicFeaturedSection from '@/components/PublicFeaturedSection';
import WeeklySpotlightBanner from '@/components/WeeklySpotlightBanner';
import AllFeaturedPartnersStrip from '@/components/AllFeaturedPartnersStrip';
import EcosystemHowItWorks from '@/components/EcosystemHowItWorks';
import NewsletterSignup from '@/components/NewsletterSignup';
import SignupWalkthrough from '@/components/SignupWalkthrough';
import LivePreviewModal from '@/components/LivePreviewModal';
import CreatorAvatar from '@/components/CreatorAvatar';
import PartnerOfTheMonth from '@/components/PartnerOfTheMonth';
import CreatorOfTheWeekCard from '@/components/CreatorOfTheWeekCard';
import { VerifiedSellerBadge } from '@/components/VerifiedSellerBadge';
import HomeFAQ, { HOMEPAGE_FAQS } from '@/components/HomeFAQ';
import PressBadge from '@/components/PressBadge';

function Home() {
  const navigate = useNavigate();
  const location = useLocation();
  const { t, i18n } = useTranslation();
  const [user, setUser] = useState(null);
  const [categories, setCategories] = useState([]);
  const [products, setProducts] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [cart, setCart] = useState(() => {
    return getStoredCart();
  });
  const [recommendations, setRecommendations] = useState([]);
  const [trending, setTrending] = useState([]);
  const [featuredCreators, setFeaturedCreators] = useState([]);
  const [creatorOfTheWeek, setCreatorOfTheWeek] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [showSearchDropdown, setShowSearchDropdown] = useState(false);
  const [storeSearchQuery, setStoreSearchQuery] = useState('');
  const [storeSearchResults, setStoreSearchResults] = useState([]);
  const [showStoreDropdown, setShowStoreDropdown] = useState(false);
  const [sortBy, setSortBy] = useState('newest');
  const [priceFilter, setPriceFilter] = useState('all');
  const [showAuthModal, setShowAuthModal] = useState(false);
  // Postcard QR welcome banner. Visible when the user arrived from a
  // scanned postcard (either via the /qr/:slug redirect chain or by
  // landing directly on /?qr=<slug>&code=POSTCARD10). Dismissed by user
  // or auto-fades on next page nav. Coupon code is shown so they can
  // copy it; it auto-applies at checkout via the X-QR-Slug interceptor.
  const [qrBanner, setQrBanner] = useState(null); // { slug, code }
  // Auto-open the AuthModal (register tab, email prefilled) when the
  // visitor arrives from the email-capture popup's "Create my buyer
  // profile" button. URL contract: /?signup=1&email=<urlEncoded>.
  // Reset after we've consumed the params so a manual refresh doesn't
  // re-trigger the modal.
  const [authPrefillEmail, setAuthPrefillEmail] = useState('');
  const [authDefaultTab, setAuthDefaultTab] = useState('login');
  const [language, setLanguage] = useState(localStorage.getItem('language') || 'en');
  const [showLanguageDropdown, setShowLanguageDropdown] = useState(false);
  const [isAdmin, setIsAdmin] = useState(false);
  const [unreadMessages, setUnreadMessages] = useState(0);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [mobileSearchOpen, setMobileSearchOpen] = useState(false);
  // Live "Tools reviewed" count for the hero trust stat — fetched once on
  // mount from /public/stats/vetted-count. Falls back to null which renders
  // the static "100%" stat if the API is unavailable.
  const [vettedCount, setVettedCount] = useState(null);
  // Live partner count for Slot 1. Fetched alongside the vetted count from
  // the same endpoint so we don't pay for a second HTTP round-trip.
  const [partnersCount, setPartnersCount] = useState(null);
  // Admin-configurable display tuning for the partners stat. The displayed
  // number is `max(floor, partnersCount - offset)`. Set from /admin so the
  // hero can match what `/featured` renders without a code change.
  const [partnerDisplayOffset, setPartnerDisplayOffset] = useState(0);
  const [partnerDisplayFloor, setPartnerDisplayFloor] = useState(0);
  const [topEarning, setTopEarning] = useState([]);
  const [justLaunched, setJustLaunched] = useState([]);
  const [previewSlug, setPreviewSlug] = useState(null);
  const [demoProducts, setDemoProducts] = useState([]);
  const [successStories, setSuccessStories] = useState([]);
  const [blogPosts, setBlogPosts] = useState([]);
  const mobileMenuRef = useRef(null);

  const languages = [
    { code: 'en', name: 'English', flag: '🇺🇸' },
    { code: 'es', name: 'Español', flag: '🇪🇸' },
    { code: 'fr', name: 'Français', flag: '🇫🇷' },
    { code: 'pt', name: 'Português', flag: '🇧🇷' },
    { code: 'de', name: 'Deutsch', flag: '🇩🇪' },
    { code: 'it', name: 'Italiano', flag: '🇮🇹' },
    { code: 'ru', name: 'Русский', flag: '🇷🇺' },
    { code: 'ja', name: '日本語', flag: '🇯🇵' },
    { code: 'ko', name: '한국어', flag: '🇰🇷' },
    { code: 'zh', name: '中文', flag: '🇨🇳' },
    { code: 'ar', name: 'العربية', flag: '🇸🇦' },
    { code: 'hi', name: 'हिन्दी', flag: '🇮🇳' },
    { code: 'tl', name: 'Tagalog', flag: '🇵🇭' }
  ];

  const handleLanguageChange = (langCode) => {
    setLanguage(langCode);
    i18n.changeLanguage(langCode);
    localStorage.setItem('language', langCode);
    setShowLanguageDropdown(false);
    const langName = languages.find(l => l.code === langCode)?.name;
    toast.success(`${t('toast.languageChanged')} ${langName}`);
  };

  // Categories sorted alphabetically by their *displayed* name.
  // Re-computes when the language changes so the visible A→Z order
  // matches the labels the user is actually seeing (e.g. switching
  // to French re-sorts "Agents IA" / "Audio IA" in the new order).
  // `localeCompare` with the active locale gives proper accent /
  // diacritic-aware ordering (é treated like e in French, etc.).
  const sortedCategories = useMemo(() => {
    const displayName = (c) => {
      const key = `categories.${(c.slug || '').replace(/-/g, '')}`;
      const tr = t(key);
      // i18next returns the key itself when no translation exists —
      // treat that as "no translation" and fall back to the raw name.
      return (tr && tr !== key) ? tr : (c.name || '');
    };
    // Normalize `i18n.language` to a BCP-47 tag. Some environments
    // return POSIX-style strings like "en-US@posix" which Intl rejects
    // with "Invalid language tag". Strip anything after `@`/`.` and
    // fall back to the engine default if normalization fails.
    const rawLang = (i18n.language || 'en').split(/[@.]/)[0];
    const safeCompare = (a, b) => {
      try {
        return a.localeCompare(b, rawLang, { sensitivity: 'base' });
      } catch {
        return a.localeCompare(b, undefined, { sensitivity: 'base' });
      }
    };
    return [...categories].sort((a, b) => safeCompare(displayName(a), displayName(b)));
  }, [categories, i18n.language, t]);


  // Auto-open the register tab of the AuthModal when arriving from the
  // email-capture popup. URL contract: /?signup=1&email=<urlEncoded>
  // (optionally from=popup for telemetry). Strips the params after
  // consuming them so a refresh doesn't re-trigger. Same setState-on-
  // mount pattern as the user-bootstrap effect a few lines below.
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    if (params.get('signup') !== '1') return;
    const em = params.get('email') || '';
    setAuthPrefillEmail(em);
    setAuthDefaultTab('signup');
    setShowAuthModal(true);
    // Scrub the params so a manual reload doesn't re-pop the modal.
    params.delete('signup');
    params.delete('email');
    params.delete('from');
    const qs = params.toString();
    navigate(location.pathname + (qs ? `?${qs}` : ''), { replace: true });
  }, [location.search]);

  // Postcard QR landing banner. Two entry paths produce the same UX:
  //   1. /qr/:slug → POST scan → redirect to /?qr=<slug>&code=POSTCARD10
  //   2. User types/pastes /?qr=<slug>&code=POSTCARD10 directly
  // Either way: persist the attribution to localStorage (so signup +
  // checkout carry the X-QR-Slug header) and show the welcome banner.
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const slug = (params.get('qr') || '').toLowerCase();
    if (!slug) return;
    const code = (params.get('code') || '').toUpperCase();
    // Inline write — keep this independent from the utility import so
    // a code-split Home chunk doesn't pull qrAttribution.js just for
    // banner state. Shape MUST match utils/qrAttribution.js.
    try {
      const rec = {
        slug,
        code: code || null,
        scanned_at: new Date().toISOString(),
        expires_at: new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toISOString(),
      };
      localStorage.setItem('tal_qr_attribution', JSON.stringify(rec));
    } catch (_) {}
    setQrBanner({ slug, code });
    // Scrub the params so a refresh doesn't loop us back into the
    // banner (localStorage already remembers the attribution).
    params.delete('qr');
    params.delete('code');
    const qs = params.toString();
    navigate(location.pathname + (qs ? `?${qs}` : ''), { replace: true });
  }, [location.search]);


  useEffect(() => {
    // Define functions inside useEffect to avoid the "accessed before declared" issue
    const loadCategories = async () => {
      try {
        const response = await axios.get('/categories');
        setCategories(response.data);
        if (response.data.length > 0) {
          // Pick the first category in the *displayed* (alphabetical) order
          // as the default tab. The `sortedCategories` memo below produces
          // the same ordering the user sees in the UI.
          const firstByDisplay = [...response.data].sort((a, b) => {
            const dn = (c) => {
              const key = `categories.${(c.slug || '').replace(/-/g, '')}`;
              const tr = t(key);
              return ((tr && tr !== key) ? tr : (c.name || '')).toLowerCase();
            };
            // Same defensive normalization as sortedCategories — POSIX
            // locale tags like "en-US@posix" otherwise throw RangeError.
            const rawLang = (i18n.language || 'en').split(/[@.]/)[0];
            try {
              return dn(a).localeCompare(dn(b), rawLang, { sensitivity: 'base' });
            } catch {
              return dn(a).localeCompare(dn(b), undefined, { sensitivity: 'base' });
            }
          })[0];
          setSelectedCategory(firstByDisplay.category_id);
        }
      } catch (error) {
        console.error('Error loading categories:', error);
      }
    };

    const loadProducts = async () => {
      try {
        const response = await axios.get('/products');
        setProducts(response.data);
      } catch (error) {
        console.error('Error loading products:', error);
      }
    };

    const loadTrending = async () => {
      try {
        const response = await axios.get('/recommendations/trending');
        setTrending(response.data);
      } catch (error) {
        console.error('Error loading trending:', error);
      }
    };

    const loadFeaturedCreators = async () => {
      try {
        const response = await axios.get('/creators/featured');
        setFeaturedCreators(response.data);
      } catch (error) {
        console.error('Error loading featured creators:', error);
      }
    };

    // Live "Tools reviewed" hero trust stat — counts every vetted listing
    // (approved/archived products + active affiliate partners). Read-only,
    // cached server-side for 60s, silently no-ops on failure so the hero
    // never shows a broken `--`.
    const loadVettedCount = async () => {
      try {
        const response = await axios.get('/public/stats/vetted-count');
        if (typeof response.data?.count === 'number') {
          setVettedCount(response.data.count);
        }
        if (typeof response.data?.partners === 'number') {
          setPartnersCount(response.data.partners);
        }
        if (typeof response.data?.partner_display_offset === 'number') {
          setPartnerDisplayOffset(response.data.partner_display_offset);
        }
        if (typeof response.data?.partner_display_floor === 'number') {
          setPartnerDisplayFloor(response.data.partner_display_floor);
        }
      } catch (error) {
        // Intentionally silent — fall back to the static stat.
      }
    };

    const loadCreatorOfTheWeek = async () => {
      try {
        const response = await axios.get('/creator-of-the-week');
        if (response.data) {
          setCreatorOfTheWeek(response.data);
        }
      } catch (error) {
        console.error('Error loading creator of the week:', error);
      }
    };

    const loadRecommendations = async () => {
      try {
        const response = await axios.get('/recommendations/personalized');
        setRecommendations(response.data);
      } catch (error) {
        console.error('Error loading recommendations:', error);
      }
    };

    if (location.state?.user) {
      setUser(location.state.user);
      localStorage.setItem('user', JSON.stringify(location.state.user));
    } else {
      const storedUser = localStorage.getItem('user');
      if (storedUser) {
        setUser(JSON.parse(storedUser));
      }
    }
    loadCategories();
    loadProducts();
    loadTrending();
    loadFeaturedCreators();
    loadCreatorOfTheWeek();
    loadVettedCount();

    // Load new homepage sections
    const loadTopEarning = async () => {
      try {
        const response = await axios.get('/products/top-earning');
        setTopEarning(response.data);
      } catch (error) { console.error('Error loading top earning:', error); }
    };
    const loadJustLaunched = async () => {
      try {
        const response = await axios.get('/products/just-launched?limit=12');
        setJustLaunched(response.data || []);
      } catch (error) { console.error('Error loading just-launched:', error); }
    };
    const loadDemoProducts = async () => {
      try {
        const response = await axios.get('/products/with-demos');
        setDemoProducts(response.data);
      } catch (error) { console.error('Error loading demo products:', error); }
    };
    const loadSuccessStories = async () => {
      try {
        const response = await axios.get('/creators/success-stories');
        setSuccessStories(response.data);
      } catch (error) { console.error('Error loading success stories:', error); }
    };
    const loadBlogPosts = async () => {
      try {
        const response = await axios.get('/blog/latest');
        setBlogPosts(response.data);
      } catch (error) { console.error('Error loading blog posts:', error); }
    };
    loadTopEarning();
    loadJustLaunched();
    loadDemoProducts();
    loadSuccessStories();
    loadBlogPosts();
    
    // Load recommendations if user exists in localStorage
    const storedUser = localStorage.getItem('user');
    if (storedUser) {
      loadRecommendations();
    }
    
    // Check admin access
    const checkAdmin = async () => {
      try {
        const response = await axios.get('/admin/check');
        setIsAdmin(response.data.is_admin);
      } catch {
        setIsAdmin(false);
      }
    };
    checkAdmin();
    
    // Check unread messages
    const checkUnreadMessages = async () => {
      try {
        const response = await axios.get('/messages/unread-count');
        setUnreadMessages(response.data.unread_count);
      } catch {
        setUnreadMessages(0);
      }
    };
    if (localStorage.getItem('token')) {
      checkUnreadMessages();
      // Poll for new messages every 30 seconds
      const interval = setInterval(checkUnreadMessages, 30000);
      return () => clearInterval(interval);
    }
    
    // Listen for cart updates from other components
    const handleCartUpdate = () => {
      setCart(getStoredCart());
    };
    window.addEventListener('cartUpdated', handleCartUpdate);
    return () => window.removeEventListener('cartUpdated', handleCartUpdate);
  }, [location.state]);

  // Function for loading products by category (used by UI)
  const loadProductsByCategory = async (categoryId = null) => {
    try {
      const url = categoryId ? `/products?category_id=${categoryId}` : '/products';
      const response = await axios.get(url);
      setProducts(response.data);
    } catch (error) {
      console.error('Error loading products:', error);
    }
  };

  const trackActivity = async (productId, action) => {
    if (user) {
      try {
        await axios.post('/activity/track', null, {
          params: { product_id: productId, action: action }
        });
      } catch (error) {
        console.error('Error tracking activity:', error);
      }
    }
  };

  const handleSearch = async (query) => {
    setSearchQuery(query);
    if (query.length > 1) {
      // Null-guard each field — some products (esp. drafts / sub-affiliate
      // imports) can be missing `name` or `description`, which would crash
      // the filter with "Cannot read properties of undefined (reading
      // toLowerCase)" and freeze the entire navbar search dropdown.
      const q = query.toLowerCase();
      const filtered = products.filter(p =>
        ((p.name || '').toLowerCase().includes(q)) ||
        ((p.description || '').toLowerCase().includes(q))
      );
      setSearchResults(filtered.slice(0, 5));
      setShowSearchDropdown(true);
    } else {
      setSearchResults([]);
      setShowSearchDropdown(false);
    }
  };

  const handleStoreSearch = async (query) => {
    setStoreSearchQuery(query);
    if (query.length > 1) {
      try {
        const response = await axios.get(`/creators/search?q=${encodeURIComponent(query)}`);
        setStoreSearchResults(response.data.slice(0, 5));
        setShowStoreDropdown(true);
      } catch (err) {
        setStoreSearchResults([]);
        setShowStoreDropdown(false);
      }
    } else {
      setStoreSearchResults([]);
      setShowStoreDropdown(false);
    }
  };

  const getFilteredProducts = (categoryProducts) => {
    let filtered = [...categoryProducts];
    
    // Price filter
    if (priceFilter === 'under50') {
      filtered = filtered.filter(p => p.price < 50);
    } else if (priceFilter === '50to100') {
      filtered = filtered.filter(p => p.price >= 50 && p.price < 100);
    } else if (priceFilter === 'over100') {
      filtered = filtered.filter(p => p.price >= 100);
    }
    
    // Sort
    if (sortBy === 'price_low') {
      filtered.sort((a, b) => a.price - b.price);
    } else if (sortBy === 'price_high') {
      filtered.sort((a, b) => b.price - a.price);
    } else if (sortBy === 'popular') {
      filtered.sort((a, b) => b.total_sales - a.total_sales);
    } else if (sortBy === 'rating') {
      filtered.sort((a, b) => b.rating - a.rating);
    } else {
      filtered.sort((a, b) => new Date(b.created_at) - new Date(a.created_at));
    }
    
    return filtered;
  };

  const handleCategoryChange = (categoryId) => {
    setSelectedCategory(categoryId);
    loadProductsByCategory(categoryId);
  };

  const handleAuthSuccess = (userData) => {
    setUser(userData);
    // Recommendations will be loaded on next page refresh
  };

  const handleGoogleLogin = () => {
    const redirectUrl = window.location.origin;
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  const handleLogout = async () => {
    try {
      await axios.post('/auth/logout');
      setUser(null);
      localStorage.removeItem('user');
      localStorage.removeItem('token');
      localStorage.removeItem('session_token');
      toast.success(t('toast.loggedOut'));
    } catch (error) {
      console.error('Logout error:', error);
      // Still clear local storage even if API fails
      setUser(null);
      localStorage.removeItem('user');
      localStorage.removeItem('token');
      localStorage.removeItem('session_token');
    }
  };

  const addToCart = (product) => {
    let updatedCart;
    const existingItem = cart.find(item => item.product_id === product.product_id);
    if (existingItem) {
      updatedCart = cart.map(item => 
        item.product_id === product.product_id 
          ? { ...item, quantity: item.quantity + 1 }
          : item
      );
    } else {
      updatedCart = [...cart, { ...product, quantity: 1 }];
    }
    setCart(updatedCart);
    saveCart(updatedCart);
    window.dispatchEvent(new Event('cartUpdated'));
    toast.success(t('toast.addedToCart'));
  };

  return (
    <div className="min-h-screen bg-[#050505] text-white">
      {qrBanner && (
        <div
          className="bg-gradient-to-r from-[#FF8C00] via-[#FFA500] to-[#FF8C00] text-black px-4 py-3 flex items-center justify-center gap-3 text-sm font-medium relative z-[60]"
          data-testid="qr-postcard-welcome-banner"
        >
          <span className="text-xs uppercase tracking-widest font-bold hidden sm:inline">Welcome from the postcard</span>
          <span className="sm:inline">10% off auto-applied at checkout — code</span>
          <code
            className="bg-black/15 px-2 py-0.5 rounded font-mono text-xs sm:text-sm font-bold tracking-wider cursor-pointer hover:bg-black/25"
            onClick={() => { try { navigator.clipboard.writeText(qrBanner.code || ''); toast.success(`Copied ${qrBanner.code}`); } catch (_) {} }}
            data-testid="qr-postcard-coupon-code"
            title="Click to copy"
          >
            {qrBanner.code || 'POSTCARD10'}
          </code>
          <button
            onClick={() => setQrBanner(null)}
            className="ml-2 p-1 rounded hover:bg-black/10"
            aria-label="Dismiss"
            data-testid="qr-postcard-banner-close"
          >
            <X className="w-4 h-4" />
          </button>
        </div>
      )}
      <SEOHead 
        title="AI Marketplace - Buy & Sell AI Tools, Software & Digital Products"
        description="Browse AI products across 52 categories. Buy AI tools, AI chatbots, AI automation software, AI prompts, and digital products from creators worldwide. Secure checkout, instant delivery."
        path="/"
        keywords="AI marketplace, buy AI tools, sell AI products, AI software store, AI chatbot marketplace, AI automation tools, AI prompt marketplace, AI business tools, AI writing tools, AI image generation, AI video tools, AI coding tools, AI data analytics, AI marketing tools, best AI tools 2026, AI digital products, AI creator platform, AI SaaS, machine learning tools, AI agents, AI templates, prompt engineering marketplace, AI tools for entrepreneurs, AI startup tools, affordable AI tools"
        schema={{
          '@context': 'https://schema.org',
          '@graph': [
            generateWebsiteSchema(),
            generateOrganizationSchema(),
            generateMarketplaceSchema(),
            generateFAQSchema(HOMEPAGE_FAQS),
          ]
        }}
      />
      <header className="border-b border-white/10 backdrop-blur-xl bg-black/60 sticky top-0 z-50" data-testid="main-header">
        <div className="container mx-auto px-4 md:px-8 lg:px-12">
          <div className="flex items-center justify-between h-14 md:h-16">
            {/* Logo */}
            <div className="flex items-center gap-2 cursor-pointer flex-shrink-0" onClick={() => navigate('/')} data-testid="logo-link">
              <Logo className="w-6 h-6 md:w-7 md:h-7" color="#00F0FF" />
              <span className="text-lg md:text-xl font-bold tracking-tight" style={{ fontFamily: 'Outfit, sans-serif' }}>
                <span className="text-[#00F0FF]">theailedger</span><span className="hidden sm:inline text-[#00F0FF]">.ai</span>
              </span>
            </div>

            {/* Desktop Search */}
            <div className="hidden lg:flex items-center gap-3 flex-1 max-w-xl mx-8">
              <div className="relative flex-1">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-neutral-500" />
                <Input
                  placeholder={t('header.search')}
                  className="pl-10 bg-white/5 border-white/10 focus:border-[#00F0FF]/50 rounded-full h-9 text-sm"
                  data-testid="search-input"
                  value={searchQuery}
                  onChange={(e) => handleSearch(e.target.value)}
                  onFocus={() => searchResults.length > 0 && setShowSearchDropdown(true)}
                />
                {showSearchDropdown && searchResults.length > 0 && (
                  <div className="absolute top-full mt-2 w-full bg-neutral-900 border border-white/10 rounded-xl overflow-hidden z-50 shadow-2xl">
                    {searchResults.map((product) => (
                      <div key={product.product_id} className="flex items-center gap-3 p-3 hover:bg-white/5 cursor-pointer border-b border-white/5 last:border-0" onClick={() => { navigate(`/product/${product.product_id}`); setShowSearchDropdown(false); setSearchQuery(''); }}>
                        <img src={getProductImage(product)} alt={product.name} className="w-10 h-10 rounded-lg object-cover" />
                        <div className="flex-1 min-w-0">
                          <p className="font-medium text-sm truncate">{product.name}</p>
                          <p className="text-xs text-[#00F0FF]">${product.price}</p>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
              <div className="relative">
                <Store className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-neutral-500" />
                <Input
                  placeholder={t('header.searchStores')}
                  className="pl-10 bg-white/5 border-white/10 focus:border-white/50 rounded-full h-9 text-sm w-[160px]"
                  data-testid="store-search-input"
                  value={storeSearchQuery}
                  onChange={(e) => handleStoreSearch(e.target.value)}
                  onFocus={() => storeSearchResults.length > 0 && setShowStoreDropdown(true)}
                />
                {showStoreDropdown && storeSearchResults.length > 0 && (
                  <div className="absolute top-full mt-2 w-full bg-neutral-900 border border-white/10 rounded-xl overflow-hidden z-50 shadow-2xl">
                    {storeSearchResults.map((store) => (
                      <div key={store.creator_id} className="flex items-center gap-3 p-3 hover:bg-white/5 cursor-pointer border-b border-white/5 last:border-0" onClick={() => { trackStorefrontClick(store.store_slug, 'search_dropdown'); navigate(`/store/${store.store_slug}`); setShowStoreDropdown(false); setStoreSearchQuery(''); }}>
                        {store.avatar ? <img src={store.avatar} alt={store.store_name} className="w-8 h-8 rounded-full object-cover" /> : <div className="w-8 h-8 rounded-full bg-white text-black/20 flex items-center justify-center"><Store className="w-4 h-4 text-white" /></div>}
                        <div className="flex-1 min-w-0">
                          <p className="font-medium text-sm truncate">{store.store_name}</p>
                          <p className="text-xs text-neutral-400">{store.total_products || 0} products</p>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>

            {/* Desktop Nav */}
            <div className="hidden md:flex items-center gap-1 lg:gap-2">
              {/* Sell CTA - always visible */}
              <Button
                onClick={() => navigate('/submit')}
                className="h-9 px-3 bg-[#00F0FF] hover:bg-[#00F0FF]/90 text-black font-semibold text-sm mr-1"
                data-testid="header-submit-cta"
              >
                Sell your AI tool
              </Button>
              {user ? (
                <>
                  {/* Cart */}
                  <Button variant="ghost" size="icon" onClick={() => navigate('/checkout')} className="relative h-9 w-9" data-testid="cart-button">
                    <ShoppingCart className="w-[18px] h-[18px]" />
                    {cart.length > 0 && <span className="absolute -top-0.5 -right-0.5 bg-[#00F0FF] text-black text-[10px] rounded-full w-4 h-4 flex items-center justify-center font-bold">{cart.length}</span>}
                  </Button>
                  {/* Messages */}
                  <Button variant="ghost" size="icon" onClick={() => navigate('/messages')} className="relative h-9 w-9" data-testid="messages-button">
                    <MessageSquare className="w-[18px] h-[18px]" />
                    {unreadMessages > 0 && <span className="absolute -top-0.5 -right-0.5 bg-red-500 text-white text-[10px] rounded-full w-4 h-4 flex items-center justify-center">{unreadMessages > 9 ? '9+' : unreadMessages}</span>}
                  </Button>
                  {/* My Purchases */}
                  <Button variant="ghost" onClick={() => navigate('/my-purchases')} className="h-9 px-3 text-sm text-neutral-300 hover:text-white" data-testid="my-purchases-button">
                    <Package className="w-4 h-4 mr-1.5" />
                    <span className="hidden xl:inline">Purchases</span>
                  </Button>
                  {/* Dashboard - for creators/admin */}
                  {(user.role === 'creator' || user.role === 'admin') && (
                    <Button onClick={() => navigate('/dashboard')} className="h-9 px-3 bg-white/10 hover:bg-white/20 text-white border border-white/10 rounded-full text-sm" data-testid="dashboard-button">
                      <Store className="w-4 h-4 mr-1.5" />
                      Dashboard
                    </Button>
                  )}
                  {/* Admin */}
                  {isAdmin && (
                    <Button variant="ghost" onClick={() => navigate('/admin')} className="h-9 px-3 text-sm text-white hover:text-neutral-300" data-testid="admin-button">
                      <Shield className="w-4 h-4 mr-1.5" />
                      Admin
                    </Button>
                  )}
                  {/* Profile + Logout */}
                  <div className="flex items-center gap-0.5 ml-1 pl-2 border-l border-white/10">
                    <Button variant="ghost" size="icon" onClick={() => navigate('/profile')} className="h-9 w-9" data-testid="profile-button"><User className="w-[18px] h-[18px]" /></Button>
                    <Button variant="ghost" size="icon" onClick={handleLogout} className="h-9 w-9 text-neutral-400 hover:text-red-400" data-testid="logout-button"><LogOut className="w-[18px] h-[18px]" /></Button>
                  </div>
                </>
              ) : (
                <>
                  {/* Language Selector */}
                  <div className="relative">
                    <Button variant="ghost" size="sm" onClick={() => setShowLanguageDropdown(!showLanguageDropdown)} className="flex items-center gap-1.5 h-9 hover:bg-white/5" data-testid="language-button">
                      <Globe className="w-4 h-4" />
                      <span className="text-lg" data-testid="current-flag">{languages.find(l => l.code === language)?.flag || '🇺🇸'}</span>
                    </Button>
                    {showLanguageDropdown && (
                      <>
                        <div className="fixed inset-0 z-40" onClick={() => setShowLanguageDropdown(false)} />
                        <div className="absolute right-0 top-full mt-2 bg-neutral-900 border border-white/10 rounded-xl overflow-hidden shadow-2xl z-50 min-w-[200px] max-h-80 overflow-y-auto">
                          {languages.map((lang) => (
                            <button key={lang.code} onClick={() => handleLanguageChange(lang.code)} className={`w-full flex items-center gap-3 px-4 py-2.5 hover:bg-white/5 transition-colors ${language === lang.code ? 'bg-[#00F0FF]/10 border-l-2 border-[#00F0FF]' : ''}`} data-testid={`lang-${lang.code}`}>
                              <span className="text-xl">{lang.flag}</span>
                              <span className="text-sm">{lang.name}</span>
                            </button>
                          ))}
                        </div>
                      </>
                    )}
                  </div>
                  <Button onClick={() => setShowAuthModal(true)} className="bg-[#00F0FF] hover:bg-[#00C2CC] text-black rounded-full px-5 h-9 text-sm font-semibold" data-testid="login-button">
                    {t('header.signIn')}
                  </Button>
                </>
              )}
            </div>

            {/* Mobile: Search + Cart + Hamburger */}
            <div className="flex items-center gap-1 md:hidden">
              <Button variant="ghost" size="icon" onClick={() => setMobileSearchOpen(!mobileSearchOpen)} className="h-9 w-9" data-testid="mobile-search-toggle">
                <Search className="w-5 h-5" />
              </Button>
              {user && (
                <Button variant="ghost" size="icon" onClick={() => navigate('/checkout')} className="relative h-9 w-9" data-testid="mobile-cart-button">
                  <ShoppingCart className="w-5 h-5" />
                  {cart.length > 0 && <span className="absolute -top-0.5 -right-0.5 bg-[#00F0FF] text-black text-[10px] rounded-full w-4 h-4 flex items-center justify-center font-bold">{cart.length}</span>}
                </Button>
              )}
              <Button variant="ghost" size="icon" onClick={() => setMobileMenuOpen(true)} className="h-9 w-9" data-testid="mobile-menu-toggle">
                <Menu className="w-5 h-5" />
              </Button>
            </div>
          </div>

          {/* Mobile Search Bar (collapsible) */}
          {mobileSearchOpen && (
            <div className="md:hidden pb-3 flex gap-2" data-testid="mobile-search-bar">
              <div className="relative flex-1">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-neutral-500" />
                <Input
                  placeholder={t('header.search')}
                  className="pl-10 bg-white/5 border-white/10 focus:border-[#00F0FF]/50 rounded-full h-9 text-sm"
                  value={searchQuery}
                  onChange={(e) => handleSearch(e.target.value)}
                  autoFocus
                />
                {showSearchDropdown && searchResults.length > 0 && (
                  <div className="absolute top-full mt-2 w-full bg-neutral-900 border border-white/10 rounded-xl overflow-hidden z-50 shadow-2xl">
                    {searchResults.map((product) => (
                      <div key={product.product_id} className="flex items-center gap-3 p-3 hover:bg-white/5 cursor-pointer" onClick={() => { navigate(`/product/${product.product_id}`); setShowSearchDropdown(false); setSearchQuery(''); setMobileSearchOpen(false); }}>
                        <img src={getProductImage(product)} alt={product.name} className="w-10 h-10 rounded-lg object-cover" />
                        <div className="flex-1 min-w-0">
                          <p className="font-medium text-sm truncate">{product.name}</p>
                          <p className="text-xs text-[#00F0FF]">${product.price}</p>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      </header>

      {/* Mobile Navigation Drawer */}
      {mobileMenuOpen && (
        <div className="fixed inset-0 z-[100] md:hidden" data-testid="mobile-nav-drawer">
          <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" onClick={() => setMobileMenuOpen(false)} />
          <div ref={mobileMenuRef} className="absolute right-0 top-0 bottom-0 w-[280px] bg-[#0a0a0a] border-l border-white/10 overflow-y-auto animate-in slide-in-from-right duration-200">
            {/* Drawer Header */}
            <div className="flex items-center justify-between p-4 border-b border-white/10">
              <span className="text-sm font-semibold text-neutral-300">Menu</span>
              <Button variant="ghost" size="icon" onClick={() => setMobileMenuOpen(false)} className="h-8 w-8" data-testid="mobile-menu-close">
                <X className="w-5 h-5" />
              </Button>
            </div>

            {user ? (
              <div className="p-3">
                {/* User Info */}
                <div className="flex items-center gap-3 p-3 mb-3 bg-white/5 rounded-xl">
                  <div className="w-10 h-10 rounded-full bg-gradient-to-br from-[#00F0FF] to-white flex items-center justify-center text-sm font-bold">{user.name?.charAt(0)?.toUpperCase() || 'U'}</div>
                  <div className="flex-1 min-w-0">
                    <p className="font-medium text-sm truncate">{user.name}</p>
                    <p className="text-xs text-neutral-400 truncate">{user.email}</p>
                  </div>
                </div>

                {/* Nav Items */}
                <nav className="space-y-0.5">
                  <MobileNavItem icon={<Store className="w-4 h-4" />} label="Sell your AI tool" accent="#00F0FF" onClick={() => { navigate('/submit'); setMobileMenuOpen(false); }} testId="mobile-submit" />
                  <MobileNavItem icon={<Package className="w-4 h-4" />} label="My Purchases" onClick={() => { navigate('/my-purchases'); setMobileMenuOpen(false); }} testId="mobile-purchases" />
                  <MobileNavItem icon={<MessageSquare className="w-4 h-4" />} label="Messages" badge={unreadMessages} onClick={() => { navigate('/messages'); setMobileMenuOpen(false); }} testId="mobile-messages" />
                  <MobileNavItem icon={<Heart className="w-4 h-4" />} label="Wishlist" onClick={() => { navigate('/wishlist'); setMobileMenuOpen(false); }} testId="mobile-wishlist" />
                  <MobileNavItem icon={<MessageSquare className="w-4 h-4" />} label="Chat Rooms" onClick={() => { navigate('/chat'); setMobileMenuOpen(false); }} testId="mobile-chat" />
                  <MobileNavItem icon={<User className="w-4 h-4" />} label="Profile" onClick={() => { navigate('/profile'); setMobileMenuOpen(false); }} testId="mobile-profile" />

                  {(user.role === 'creator' || user.role === 'admin') && (
                    <>
                      <div className="my-2 border-t border-white/10" />
                      <MobileNavItem icon={<Store className="w-4 h-4" />} label="Seller Dashboard" accent="#00F0FF" onClick={() => { navigate('/dashboard'); setMobileMenuOpen(false); }} testId="mobile-dashboard" />
                    </>
                  )}
                  {isAdmin && (
                    <MobileNavItem icon={<Shield className="w-4 h-4" />} label="Admin Console" accent="#ef4444" onClick={() => { navigate('/admin'); setMobileMenuOpen(false); }} testId="mobile-admin" />
                  )}
                </nav>

                <div className="mt-4 pt-3 border-t border-white/10">
                  <button onClick={() => { handleLogout(); setMobileMenuOpen(false); }} className="w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-red-400 hover:bg-red-500/10 transition-colors text-sm" data-testid="mobile-logout">
                    <LogOut className="w-4 h-4" />
                    Sign Out
                  </button>
                </div>
              </div>
            ) : (
              <div className="p-3">
                <Button onClick={() => { navigate('/submit'); setMobileMenuOpen(false); }} className="w-full bg-[#00F0FF] hover:bg-[#00F0FF]/90 text-black font-semibold rounded-full h-10 mb-2" data-testid="mobile-submit-button">
                  Sell your AI tool
                </Button>
                <Button onClick={() => { setShowAuthModal(true); setMobileMenuOpen(false); }} variant="outline" className="w-full border-white/20 text-white hover:bg-white/5 rounded-full h-10 mb-4" data-testid="mobile-login-button">
                  {t('header.signIn')}
                </Button>
                {/* Language selector */}
                <p className="text-xs text-neutral-500 font-medium mb-2 px-3">LANGUAGE</p>
                <div className="grid grid-cols-2 gap-1">
                  {languages.map((lang) => (
                    <button key={lang.code} onClick={() => { handleLanguageChange(lang.code); setMobileMenuOpen(false); }} className={`flex items-center gap-2 px-3 py-2 rounded-lg text-sm transition-colors ${language === lang.code ? 'bg-[#00F0FF]/10 text-[#00F0FF]' : 'hover:bg-white/5 text-neutral-400'}`}>
                      <span className="text-base">{lang.flag}</span>
                      <span className="truncate">{lang.name}</span>
                    </button>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      <section className="relative py-20 md:py-28 px-6 md:px-12 overflow-hidden">
        <div className="hero-glow absolute inset-0 pointer-events-none"></div>
        <div className="container mx-auto max-w-5xl text-center relative z-10">
          <h2 className="text-4xl sm:text-5xl lg:text-6xl font-black mb-4 text-white leading-tight" style={{ fontFamily: 'Outfit, sans-serif' }}>
            {t('hero.title')}
            <br />
            <span className="text-white">{t('hero.titleHighlight')}</span>
          </h2>
          <p className="text-base md:text-lg text-neutral-300 max-w-2xl mx-auto mb-3 leading-relaxed">
            {t('hero.subtitle')}
          </p>
          {/* Business-operator tagline — small italic line that welcomes
              non-creator visitors without hijacking the main H1. Placement A
              per founder request: sits between the main subtitle and the
              As-Seen-On press badge so it reads as a "who this is for"
              note rather than a competing headline. */}
          <p
            className="text-xs md:text-sm italic text-neutral-400 max-w-2xl mx-auto mb-8 leading-relaxed"
            data-testid="hero-operator-tagline"
          >
            AI that pays for itself — picked for serious operators. Every tool reviewed before it&apos;s listed. See real results from businesses already using it.
          </p>

          {/* Split User Paths */}
          {!user && (
            <div className="flex flex-col sm:flex-row items-center justify-center gap-3 sm:gap-4 mb-6" data-testid="hero-split-paths">
              <Button
                onClick={() => setShowAuthModal(true)}
                className="bg-[#00F0FF] hover:bg-[#00D4E0] text-black rounded-full px-8 py-6 text-base font-bold w-full sm:w-auto gap-2"
                data-testid="hero-explore-button"
              >
                <Search className="w-4 h-4" />
                {t('hero.getStarted')}
              </Button>
              <Button
                onClick={() => { setShowAuthModal(true); }}
                variant="outline"
                className="border-white/50 hover:bg-[#00F0FF]/20 text-[#00F0FF] hover:text-white rounded-full px-8 py-6 text-base font-bold w-full sm:w-auto gap-2"
                data-testid="hero-sell-button"
              >
                <Store className="w-4 h-4" />
                {t('hero.startSelling')}
              </Button>
            </div>
          )}
          {!user && (
            <p className="text-xs text-neutral-500 mb-8">{t('hero.sellSubtext')}</p>
          )}

          {/* As Seen On Badge — shared <PressBadge /> component so markup
              stays in sync with the pre-launch ComingSoon variant. WhyIQ
              Phase 2 fix #6: unverifiable media logos erode trust; clicking
              the badge routes to /press where every article is listed. */}
          <PressBadge
            onClick={() => { navigate('/press') }}
          />

          {/* Social Proof Stats */}
          <div className="flex flex-wrap items-center justify-center gap-6 md:gap-10" data-testid="hero-social-proof">
            <button
              type="button"
              onClick={() => navigate('/featured')}
              className="text-center group cursor-pointer hover:opacity-80 transition-opacity focus:outline-none focus:ring-2 focus:ring-[#00F0FF]/50 rounded-lg px-2 -mx-2"
              data-testid="hero-partners-stat"
              aria-label="Browse all featured AI partners"
            >
              <p className="text-2xl md:text-3xl font-black text-white group-hover:text-[#00F0FF] transition-colors" data-testid="hero-partners-count">
                {/* Display logic is admin-configurable via
                    PATCH /api/admin/hero-stats-config — keeps the hero in
                    sync with what /featured actually renders without a
                    code change. Today: offset=4, floor=48 (see
                    /admin/hero-stats UI). Hardcoded fallback is `48` so the
                    hero never shows the AI/agent-confusing `Marblism +`
                    string if the API ever stalls on first paint. */}
                {partnersCount !== null
                  ? Math.max(partnerDisplayFloor, partnersCount - partnerDisplayOffset).toLocaleString()
                  : '48'}
              </p>
              <p className="text-xs text-neutral-500 mt-0.5">Featured AI partners</p>
              <p className="text-[10px] text-[#00F0FF]/70 mt-0.5">Browse all →</p>
            </button>
            <div className="w-px h-8 bg-white/10 hidden sm:block" />
            <div className="text-center">
              <p className="text-2xl md:text-3xl font-black text-white">52</p>
              <p className="text-xs text-neutral-500 mt-0.5">AI categories</p>
            </div>
            <div className="w-px h-8 bg-white/10 hidden sm:block" />
            <div className="text-center">
              <p className="text-2xl md:text-3xl font-black text-white" data-testid="hero-vetted-count">
                {vettedCount !== null ? vettedCount.toLocaleString() : '100%'}
              </p>
              <p className="text-xs text-neutral-500 mt-0.5">
                {vettedCount !== null ? 'Tools reviewed' : 'Human-reviewed listings'}
              </p>
              <p className="text-[10px] text-neutral-600 mt-0.5">
                {vettedCount !== null ? '100% by humans · No bots' : 'No bots · No scraped junk'}
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Auth Modal */}
      <AuthModal 
        isOpen={showAuthModal} 
        onClose={() => setShowAuthModal(false)}
        onSuccess={handleAuthSuccess}
        defaultTab={authDefaultTab}
        prefillEmail={authPrefillEmail}
      />

      {/* Ask Scout — conversational product discovery. Shows visitors a
          direct path from "I have a problem" to "here are the 2 products
          that solve it" without making them browse the catalog. */}
      <section
        className="px-4 sm:px-6 md:px-12 pt-12 md:pt-16 pb-4"
        data-testid="home-scout-section"
      >
        <div className="container mx-auto max-w-4xl">
          <div className="flex items-center gap-2 justify-center mb-3">
            <Sparkles className="w-4 h-4 text-cyan-400" />
            <span className="text-cyan-300 text-xs font-bold tracking-widest uppercase">
              Ask Scout
            </span>
          </div>
          <h2 className="text-2xl sm:text-3xl font-bold text-white text-center mb-2">
            Not sure where to start?
          </h2>
          <p className="text-neutral-400 text-sm text-center mb-7 max-w-2xl mx-auto">
            Tell Scout what you're trying to get done and it'll surface the 2
            or 3 tools on the platform that actually fit — with honest pros
            and cons.
          </p>
          <ScoutDiscover variant="hero" />
        </div>
      </section>

      {/* Featured AI Tools — social proof section, auto-populates from
          /api/affiliate-partners. Rendered above the product category tabs
          so visitors see recognizable brands before they see our catalog. */}
      <div className="container mx-auto px-4 sm:px-6 md:px-12 max-w-3xl pt-12 md:pt-16">
        <WeeklySpotlightBanner />
      </div>
      <PublicFeaturedSection />
      <AllFeaturedPartnersStrip />

      {/* Top Featured Partner this month — auto-populates from the
          /api/partner-of-the-month endpoint (revenue → clicks → all-time
          waterfall). Renders nothing if no winner can be determined,
          so the layout never breaks. */}
      <PartnerOfTheMonth />

      {/* Visual explainer — landing-page-only (signed-in users already
          inside the marketplace don't need the "this is an ecosystem"
          pitch they've already converted on). */}
      {!user && <EcosystemHowItWorks />}

      {/* JVZoo-style 4-step signup walkthrough — landing-page-only.
          Signed-in users have their own dashboard onboarding, so this
          section is hidden once authenticated. */}
      {!user && <SignupWalkthrough />}
      <section className="container mx-auto px-4 md:px-12 py-6 md:py-8">
        <Tabs value={selectedCategory} onValueChange={handleCategoryChange} className="w-full" data-testid="category-tabs">
          {/* Horizontal scrollable categories on mobile */}
          <div className="overflow-x-auto -mx-4 px-4 md:mx-0 md:px-0 scrollbar-hide">
            <TabsList className="bg-neutral-900/50 border border-white/10 p-3 md:p-4 rounded-2xl mb-4 md:mb-6 h-auto flex md:flex-wrap md:justify-center gap-2 min-w-max md:min-w-0">
              {sortedCategories.map((cat) => (
                <TabsTrigger
                  key={cat.category_id}
                  value={cat.category_id}
                  className="rounded-full px-3 md:px-4 py-1.5 md:py-2 text-xs md:text-sm data-[state=active]:bg-[#00F0FF] data-[state=active]:text-black font-semibold whitespace-nowrap"
                  data-testid={`category-tab-${cat.slug}`}
                >
                  {t(`categories.${(cat.slug || '').replace(/-/g, '')}`) || cat.name || 'Category'}
                </TabsTrigger>
              ))}
            </TabsList>
          </div>

          {/* Filters and Sort - Separate section below categories */}
          <div className="flex flex-col sm:flex-row sm:flex-wrap items-start sm:items-center justify-between gap-3 md:gap-4 mb-4 md:mb-6 p-3 md:p-4 bg-neutral-900/30 rounded-xl border border-white/5">
            <div className="flex flex-wrap items-center gap-2 md:gap-3 w-full sm:w-auto">
              <span className="text-xs md:text-sm text-neutral-400 font-semibold">{t('filters.filter')}</span>
              <select
                value={priceFilter}
                onChange={(e) => setPriceFilter(e.target.value)}
                className="bg-neutral-800 border border-white/10 rounded-lg px-2 md:px-3 py-1.5 md:py-2 text-xs md:text-sm focus:border-[#00F0FF]/50 focus:outline-none flex-1 sm:flex-none"
                data-testid="price-filter"
              >
                <option value="all">{t('filters.allPrices')}</option>
                <option value="under50">{t('filters.under50')}</option>
                <option value="50to100">{t('filters.50to100')}</option>
                <option value="over100">{t('filters.over100')}</option>
              </select>
            </div>
            
            <div className="flex items-center gap-2 md:gap-3 w-full sm:w-auto">
              <span className="text-xs md:text-sm text-neutral-400 font-semibold">{t('filters.sortBy')}</span>
              <select
                value={sortBy}
                onChange={(e) => setSortBy(e.target.value)}
                className="bg-neutral-800 border border-white/10 rounded-lg px-2 md:px-3 py-1.5 md:py-2 text-xs md:text-sm focus:border-[#00F0FF]/50 focus:outline-none flex-1 sm:flex-none"
                data-testid="sort-select"
              >
                <option value="newest">{t('filters.newest')}</option>
                <option value="popular">{t('filters.popular')}</option>
                <option value="rating">{t('filters.rating')}</option>
                <option value="price_low">{t('filters.priceLow')}</option>
                <option value="price_high">{t('filters.priceHigh')}</option>
              </select>
            </div>
          </div>

          {sortedCategories.map((cat) => (
            <TabsContent key={cat.category_id} value={cat.category_id} data-testid={`category-content-${cat.slug}`}>
              {/* Horizontal scroll on mobile, grid on desktop */}
              <div className="overflow-x-auto -mx-4 px-4 md:mx-0 md:px-0 md:overflow-visible scrollbar-hide">
                <div className="flex md:grid md:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-6 min-w-max md:min-w-0">
                  {getFilteredProducts(products.filter(p => p.category_id === cat.category_id)).length === 0 ? (
                    <div className="w-full text-center py-20 text-neutral-400">
                      {t('product.noProducts')}
                    </div>
                  ) : (
                    getFilteredProducts(products.filter(p => p.category_id === cat.category_id))
                      .map((product) => (
                        <div
                          key={product.product_id}
                          className="group relative rounded-2xl bg-neutral-900/50 border border-white/5 hover:border-[#00F0FF]/50 overflow-hidden card-hover w-[280px] md:w-auto flex-shrink-0 md:flex-shrink"
                          data-testid={`product-card-${product.product_id}`}
                        >
                        <div 
                          className="aspect-video bg-neutral-800 overflow-hidden cursor-pointer relative"
                          onClick={() => {
                            trackActivity(product.product_id, 'view');
                            navigate(`/product/${product.product_id}`);
                          }}
                        >
                          <ResilientImg src={getProductImage(product)} alt={product.name} className="w-full h-full object-cover" />
                          {product.video_demo_url && (
                            <div className="absolute top-3 right-3 bg-[#00F0FF] text-black px-2 py-1 rounded-full text-xs font-bold flex items-center gap-1">
                              <Play className="w-3 h-3" />
                              DEMO
                            </div>
                          )}
                          {product.total_sales >= 50 && (
                            <div className="absolute top-3 left-3 bg-white/90 text-black px-2 py-1 rounded-full text-xs font-bold flex items-center gap-1">
                              <Flame className="w-3 h-3 text-orange-500" />
                              Trending
                            </div>
                          )}
                        </div>
                        <div className="p-6">
                          <h3 
                            className="font-bold text-xl mb-2 cursor-pointer hover:text-[#00F0FF]"
                            onClick={() => {
                              trackActivity(product.product_id, 'view');
                              navigate(`/product/${product.product_id}`);
                            }}
                            data-testid={`product-name-${product.product_id}`}
                          >
                            {product.name}
                            {product.verified && (
                              <BadgeCheck className="w-4 h-4 text-[#00F0FF] inline ml-1.5 -mt-0.5" />
                            )}
                          </h3>
                          <p className="text-neutral-400 text-sm mb-4 line-clamp-2">{product.description}</p>
                          {product.is_verified_seller && (
                            <div className="mb-3">
                              <VerifiedSellerBadge verified={true} size="sm" />
                            </div>
                          )}
                          <div className="flex items-center gap-2 mb-4">
                            <div className="flex items-center">
                              <Star className="w-4 h-4 fill-[#00F0FF] text-[#00F0FF]" />
                              <span className="text-sm font-semibold ml-1">{product.rating}</span>
                            </div>
                            <span className="text-xs text-neutral-500">({product.total_reviews} {t('product.reviews')})</span>
                            {product.total_sales >= 10 && (
                              <span className="text-xs text-neutral-400 flex items-center gap-1 ml-auto">
                                <Users className="w-3 h-3" />
                                {product.total_sales}+ buyers
                              </span>
                            )}
                            {product.total_sales < 10 && (
                              <span className="text-xs text-neutral-500 ml-auto">{product.total_sales} {t('product.sales')}</span>
                            )}
                          </div>
                          <div className="flex items-center justify-between">
                            <span className="text-2xl font-bold text-[#00F0FF]" data-testid={`product-price-${product.product_id}`}>
                              ${product.price}
                            </span>
                            <Button
                              onClick={() => {
                                if (user) {
                                  addToCart(product);
                                } else {
                                  toast.error(t('toast.signInToAddCart'));
                                }
                              }}
                              className="bg-white hover:bg-neutral-200 text-black rounded-full"
                              data-testid={`add-to-cart-${product.product_id}`}
                            >
                              {t('product.addToCart')}
                            </Button>
                          </div>
                        </div>
                      </div>
                    ))
                )}
                </div>
              </div>
            </TabsContent>
          ))}
        </Tabs>
      </section>

      {/* AI-Powered Recommendations */}
      {user && recommendations.length > 0 && (
        <section className="container mx-auto px-4 md:px-12 py-8 md:py-12 border-t border-white/10">
          <div className="flex items-center gap-3 mb-6 md:mb-8">
            <Logo className="w-5 h-5 md:w-6 md:h-6" color="#00F0FF" />
            <h2 className="text-2xl md:text-3xl font-bold" style={{ fontFamily: 'Outfit, sans-serif' }}>
              {t('recommendations.title')} <span className="text-white">{t('recommendations.titleHighlight')}</span>
            </h2>
          </div>
          <p className="text-neutral-400 mb-6 md:mb-8 text-sm md:text-base">{t('recommendations.subtitle')}</p>
          {/* Horizontal scroll on mobile */}
          <div className="overflow-x-auto -mx-4 px-4 md:mx-0 md:px-0 md:overflow-visible scrollbar-hide">
            <div className="flex md:grid md:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-6 min-w-max md:min-w-0">
            {recommendations.map((product) => (
              <div
                key={product.product_id}
                className="group relative rounded-2xl bg-neutral-900/50 border border-white/20 hover:border-white/50 overflow-hidden card-hover w-[280px] md:w-auto flex-shrink-0 md:flex-shrink"
              >
                <div 
                  className="aspect-video bg-neutral-800 overflow-hidden cursor-pointer"
                  onClick={() => {
                    trackActivity(product.product_id, 'view');
                    navigate(`/product/${product.product_id}`);
                  }}
                >
                  <ResilientImg src={getProductImage(product)} alt={product.name} className="w-full h-full object-cover" />
                </div>
                <div className="p-4 md:p-6">
                  <h3 
                    className="font-bold text-lg md:text-xl mb-2 cursor-pointer hover:text-white"
                    onClick={() => {
                      trackActivity(product.product_id, 'view');
                      navigate(`/product/${product.product_id}`);
                    }}
                  >
                    {product.name}
                  </h3>
                  <p className="text-neutral-400 text-xs md:text-sm mb-3 md:mb-4 line-clamp-2">{product.description}</p>
                  <div className="flex items-center justify-between">
                    <span className="text-xl md:text-2xl font-bold text-[#00F0FF]" data-testid={`recommendation-price-${product.product_id}`}>
                      ${product.price}
                    </span>
                    <Button
                      onClick={() => addToCart(product)}
                      className="bg-white hover:bg-neutral-200 text-black rounded-full text-sm"
                    >
                      {t('products.addToCart')}
                    </Button>
                  </div>
                </div>
              </div>
            ))}
            </div>
          </div>
        </section>
      )}

      {/* Just Launched */}
      {justLaunched.length > 0 && (
        <section className="container mx-auto px-4 md:px-12 py-8 md:py-12 border-t border-white/10" data-testid="just-launched-section">
          <div className="flex items-start md:items-center justify-between gap-4 mb-6 md:mb-8 flex-col md:flex-row">
            <div className="flex items-center gap-3">
              <div className="text-xl md:text-2xl">✨</div>
              <h2 className="text-2xl md:text-3xl font-bold" style={{ fontFamily: 'Outfit, sans-serif' }}>
                Just <span className="text-[#00F0FF]">Launched</span>
              </h2>
              <span className="hidden md:inline text-xs uppercase tracking-wider text-neutral-500 ml-2">New this week</span>
            </div>
            <Button
              onClick={() => navigate('/submit')}
              size="sm"
              variant="outline"
              className="border-[#00F0FF]/40 text-[#00F0FF] hover:bg-[#00F0FF] hover:text-black rounded-full text-xs md:text-sm group"
              data-testid="just-launched-submit-cta"
            >
              <Zap className="w-3.5 h-3.5 mr-1.5 group-hover:scale-110 transition" />
              Submit yours → land here next
            </Button>
          </div>
          <p className="text-neutral-400 mb-6 md:mb-8 text-sm md:text-base">
            The newest tools just listed on the marketplace.
          </p>
          <div className="overflow-x-auto -mx-4 px-4 md:mx-0 md:px-0 md:overflow-visible scrollbar-hide">
            <div className="flex md:grid md:grid-cols-2 lg:grid-cols-4 gap-4 md:gap-6 min-w-max md:min-w-0">
              {justLaunched.slice(0, 8).map((product) => (
                <div
                  key={product.product_id}
                  className="group relative rounded-2xl bg-neutral-900/50 border border-[#00F0FF]/20 hover:border-[#00F0FF]/50 overflow-hidden card-hover w-[220px] md:w-auto flex-shrink-0 md:flex-shrink"
                  data-testid={`just-launched-card-${product.product_id}`}
                >
                  <div
                    className="aspect-video bg-neutral-800 overflow-hidden cursor-pointer relative"
                    onClick={() => {
                      trackActivity(product.product_id, 'view');
                      navigate(`/product/${product.product_id}`);
                    }}
                  >
                    <ResilientImg src={getProductImage(product)} alt={product.name} className="w-full h-full object-cover" />
                    <span className="absolute top-2 right-2 bg-[#00F0FF] text-black text-[10px] font-bold px-1.5 py-0.5 rounded">
                      NEW
                    </span>
                  </div>
                  <div className="p-3 md:p-4">
                    <h3
                      className="font-bold text-base md:text-lg mb-1 cursor-pointer hover:text-[#00F0FF] line-clamp-1"
                      onClick={() => {
                        trackActivity(product.product_id, 'view');
                        navigate(`/product/${product.product_id}`);
                      }}
                    >
                      {product.name}
                    </h3>
                    {product.tagline && (
                      <p className="text-neutral-400 text-xs line-clamp-2 mb-2 min-h-[2rem]">{product.tagline}</p>
                    )}
                    <div className="flex items-center justify-between mt-2 md:mt-3">
                      <span className="text-lg md:text-xl font-bold text-[#00F0FF]">
                        ${product.price}{product.is_subscription ? <span className="text-xs text-neutral-400">/mo</span> : ''}
                      </span>
                      <Button
                        onClick={() => {
                          if (user) {
                            addToCart(product);
                          } else {
                            toast.error(t('toast.signInToAddCart'));
                          }
                        }}
                        size="sm"
                        className="bg-white hover:bg-neutral-200 text-black rounded-full text-xs"
                        data-testid={`just-launched-add-${product.product_id}`}
                      >
                        {t('products.add')}
                      </Button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Trending Products */}
      {trending.length > 0 && (
        <section className="container mx-auto px-4 md:px-12 py-8 md:py-12 border-t border-white/10">
          <div className="flex items-center gap-3 mb-6 md:mb-8">
            <div className="text-xl md:text-2xl">🔥</div>
            <h2 className="text-2xl md:text-3xl font-bold" style={{ fontFamily: 'Outfit, sans-serif' }}>
              {t('trending.title')} <span className="text-white">{t('trending.titleHighlight')}</span>
            </h2>
          </div>
          <p className="text-neutral-400 mb-6 md:mb-8 text-sm md:text-base">{t('trending.subtitle')}</p>
          {/* Horizontal scroll on mobile */}
          <div className="overflow-x-auto -mx-4 px-4 md:mx-0 md:px-0 md:overflow-visible scrollbar-hide">
            <div className="flex md:grid md:grid-cols-2 lg:grid-cols-4 gap-4 md:gap-6 min-w-max md:min-w-0">
              {trending.slice(0, 4).map((product) => (
                <div
                  key={product.product_id}
                  className="group relative rounded-2xl bg-neutral-900/50 border border-[#00F0FF]/20 hover:border-[#00F0FF]/50 overflow-hidden card-hover w-[220px] md:w-auto flex-shrink-0 md:flex-shrink"
                >
                  <div 
                    className="aspect-video bg-neutral-800 overflow-hidden cursor-pointer"
                    onClick={() => {
                      trackActivity(product.product_id, 'view');
                      navigate(`/product/${product.product_id}`);
                    }}
                  >
                    <ResilientImg src={getProductImage(product)} alt={product.name} className="w-full h-full object-cover" />
                  </div>
                  <div className="p-3 md:p-4">
                    <h3 
                      className="font-bold text-base md:text-lg mb-1 cursor-pointer hover:text-[#00F0FF] line-clamp-1"
                      onClick={() => {
                        trackActivity(product.product_id, 'view');
                        navigate(`/product/${product.product_id}`);
                      }}
                    >
                      {product.name}
                      {product.verified && (
                        <BadgeCheck className="w-3.5 h-3.5 text-[#00F0FF] inline ml-1 -mt-0.5" />
                      )}
                    </h3>
                    <div className="flex items-center justify-between mt-2 md:mt-3">
                      <span className="text-lg md:text-xl font-bold text-[#00F0FF]">
                        ${product.price}
                      </span>
                      <Button
                        onClick={() => {
                          if (user) {
                            addToCart(product);
                          } else {
                            toast.error(t('toast.signInToAddCart'));
                          }
                        }}
                        size="sm"
                        className="bg-white hover:bg-neutral-200 text-black rounded-full text-xs"
                      >
                        {t('products.add')}
                      </Button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Creator of the Week Spotlight */}
      <CreatorOfTheWeekCard creator={creatorOfTheWeek} />

      {/* Featured Creators Section */}
      {featuredCreators.length > 0 && (
        <section className="container mx-auto px-4 md:px-12 py-8 md:py-12 border-t border-white/10">
          <div className="flex items-center gap-3 mb-6 md:mb-8">
            <div className="text-xl md:text-2xl">⭐</div>
            <h2 className="text-2xl md:text-3xl font-bold" style={{ fontFamily: 'Outfit, sans-serif' }}>
              {t('featuredCreators.title')} <span className="text-white">{t('featuredCreators.titleHighlight')}</span>
            </h2>
          </div>
          <p className="text-neutral-400 mb-6 md:mb-8 text-sm md:text-base">{t('featuredCreators.subtitle')}</p>
          <div className="overflow-x-auto -mx-4 px-4 md:mx-0 md:px-0 md:overflow-visible scrollbar-hide">
            <div className="flex md:grid md:grid-cols-2 lg:grid-cols-5 gap-4 md:gap-6 min-w-max md:min-w-0">
              {featuredCreators.map((creator) => (
                <div
                  key={creator.creator_id}
                  onClick={() => { trackStorefrontClick(creator.store_slug, 'home_creators_grid'); navigate(`/store/${creator.store_slug}`); }}
                  className="group relative rounded-2xl bg-neutral-900/50 border border-white/20 hover:border-white/50 overflow-hidden cursor-pointer card-hover w-[180px] md:w-auto flex-shrink-0 md:flex-shrink p-4 text-center"
                  data-testid={`featured-creator-${creator.creator_id}`}
                >
                  <CreatorAvatar
                    storeName={creator.store_name}
                    avatarUrl={creator.avatar}
                    size={64}
                    className="mx-auto rounded-full mb-3"
                  />
                  <h3 className="font-bold text-sm md:text-base mb-1 line-clamp-1 group-hover:text-white transition-colors">
                    {creator.store_name}
                  </h3>
                  <p className="text-neutral-400 text-xs mb-2 line-clamp-2">
                    {creator.bio || 'AI Product Creator'}
                  </p>
                  <div className="flex items-center justify-center gap-3 text-xs text-neutral-500">
                    <span>{creator.product_count || 0} {t('featuredCreators.products')}</span>
                    <span>•</span>
                    <span>${(creator.total_sales || 0).toLocaleString()} {t('featuredCreators.sales')}</span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>
      )}


      {/* Top Earning Products */}
      {topEarning.length > 0 && (
        <section className="container mx-auto px-4 md:px-12 py-8 md:py-12 border-t border-white/10" data-testid="top-earning-section">
          <div className="flex items-center gap-3 mb-6 md:mb-8">
            <div className="flex items-center gap-2 px-3 py-1.5 rounded-full border border-white/30 bg-white/5">
              <DollarSign className="w-4 h-4 text-white" />
              <span className="text-xs font-semibold tracking-wider uppercase text-white">Top Earners</span>
            </div>
          </div>
          <h2 className="text-2xl md:text-3xl font-bold mb-2" style={{ fontFamily: 'Outfit, sans-serif' }}>
            Products Making Real Money
          </h2>
          <p className="text-neutral-400 mb-6 md:mb-8 text-sm md:text-base">The highest revenue AI products on the platform right now.</p>
          <div className="overflow-x-auto -mx-4 px-4 md:mx-0 md:px-0 md:overflow-visible scrollbar-hide">
            <div className="flex md:grid md:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-6 min-w-max md:min-w-0">
              {topEarning.map((product) => (
                <div
                  key={product.product_id}
                  className="group relative rounded-2xl bg-neutral-900/50 border border-white/5 hover:border-white/20 overflow-hidden card-hover w-[280px] md:w-auto flex-shrink-0 md:flex-shrink"
                  data-testid={`top-earning-card-${product.product_id}`}
                >
                  <div
                    className="aspect-video bg-neutral-800 overflow-hidden cursor-pointer relative"
                    onClick={() => { trackActivity(product.product_id, 'view'); navigate(`/product/${product.product_id}`); }}
                  >
                    <ResilientImg src={getProductImage(product)} alt={product.name} className="w-full h-full object-cover" />
                    <div className="absolute top-3 left-3 bg-white/90 text-black px-2.5 py-1 rounded-full text-xs font-bold flex items-center gap-1">
                      <TrendingUp className="w-3 h-3 text-green-600" />
                      Top Earner
                    </div>
                    {product.total_sales >= 10 && (
                      <div className="absolute bottom-3 right-3 bg-black/70 backdrop-blur-sm text-white px-2 py-1 rounded-full text-xs font-medium flex items-center gap-1">
                        <Users className="w-3 h-3" />
                        {product.total_sales}+ buyers
                      </div>
                    )}
                  </div>
                  <div className="p-5">
                    <h3
                      className="font-bold text-lg mb-1.5 cursor-pointer hover:text-[#00F0FF] line-clamp-1"
                      onClick={() => { trackActivity(product.product_id, 'view'); navigate(`/product/${product.product_id}`); }}
                    >
                      {product.name}
                      {product.verified && <BadgeCheck className="w-4 h-4 text-[#00F0FF] inline ml-1.5 -mt-0.5" />}
                    </h3>
                    <p className="text-neutral-400 text-sm mb-3 line-clamp-2">{product.description}</p>
                    <div className="flex items-center justify-between">
                      <span className="text-xl font-bold text-[#00F0FF]">${product.price}</span>
                      <Button
                        onClick={() => { if (user) { addToCart(product); } else { toast.error(t('toast.signInToAddCart')); } }}
                        className="bg-white hover:bg-neutral-200 text-black rounded-full text-sm"
                        data-testid={`top-earning-add-${product.product_id}`}
                      >
                        {t('product.addToCart')}
                      </Button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Creator Success Stories */}
      {successStories.length > 0 && (
        <section className="container mx-auto px-4 md:px-12 py-8 md:py-12 border-t border-white/10" data-testid="success-stories-section">
          <div className="flex items-center gap-3 mb-6 md:mb-8">
            <div className="flex items-center gap-2 px-3 py-1.5 rounded-full border border-white/30 bg-white/5">
              <Award className="w-4 h-4 text-white" />
              <span className="text-xs font-semibold tracking-wider uppercase text-white">Success Stories</span>
            </div>
          </div>
          <h2 className="text-2xl md:text-3xl font-bold mb-2" style={{ fontFamily: 'Outfit, sans-serif' }}>
            Creators Earning on theailedger.ai
          </h2>
          <p className="text-neutral-400 mb-6 md:mb-8 text-sm md:text-base">Real creators, real revenue. See what's possible on our platform.</p>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 md:gap-6">
            {successStories.map((creator) => (
              <div
                key={creator.user_id || creator.store_slug}
                onClick={() => { if (creator.store_slug) { trackStorefrontClick(creator.store_slug, 'home_featured_creators'); navigate(`/store/${creator.store_slug}`); } }}
                className="group rounded-2xl bg-neutral-900/50 border border-white/5 hover:border-white/20 p-5 cursor-pointer transition-all duration-200"
                data-testid={`success-story-${creator.store_slug}`}
              >
                <div className="flex items-center gap-3 mb-4">
                  <CreatorAvatar
                    storeName={creator.store_name || creator.name}
                    avatarUrl={creator.avatar}
                    size={48}
                    className="rounded-full flex-shrink-0"
                  />
                  <div className="min-w-0">
                    <h4 className="font-bold text-sm text-white truncate group-hover:text-[#00F0FF] transition-colors">{creator.store_name || creator.name}</h4>
                    <p className="text-xs text-neutral-500">{creator.product_count} products listed</p>
                  </div>
                </div>
                <div className="bg-white/[0.03] rounded-lg p-3 mb-3">
                  <p className="text-xs text-neutral-500 uppercase tracking-wide mb-1">Est. Earnings</p>
                  <p className="text-xl font-bold text-white">${creator.estimated_earnings?.toLocaleString() || '—'}</p>
                </div>
                <p className="text-xs text-neutral-400 line-clamp-2">{creator.bio || 'Building and selling AI products on theailedger.ai'}</p>
              </div>
            ))}
          </div>
          <div className="text-center mt-6">
            <Button
              onClick={() => navigate('/leaderboard')}
              variant="outline"
              className="border-white/20 hover:bg-white/5 rounded-full px-6 h-10 text-sm gap-2"
              data-testid="view-leaderboard-btn"
            >
              <Trophy className="w-4 h-4" />
              View Full Leaderboard
            </Button>
          </div>
        </section>
      )}
      {demoProducts.length > 0 && (
        <section className="container mx-auto px-4 md:px-12 py-8 md:py-12 border-t border-white/10" data-testid="try-before-buy-section">
          <div className="flex items-center gap-3 mb-6 md:mb-8">
            <div className="flex items-center gap-2 px-3 py-1.5 rounded-full border border-white/30 bg-white/5">
              <Eye className="w-4 h-4 text-white" />
              <span className="text-xs font-semibold tracking-wider uppercase text-white">Try Before You Buy</span>
            </div>
          </div>
          <h2 className="text-2xl md:text-3xl font-bold mb-2" style={{ fontFamily: 'Outfit, sans-serif' }}>
            Preview Products Before Purchasing
          </h2>
          <p className="text-neutral-400 mb-6 md:mb-8 text-sm md:text-base">These products offer live demos so you can try them out first.</p>
          <div className="overflow-x-auto -mx-4 px-4 md:mx-0 md:px-0 md:overflow-visible scrollbar-hide">
            <div className="flex md:grid md:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-6 min-w-max md:min-w-0">
              {demoProducts.map((product) => (
                <div
                  key={product.product_id}
                  className="group relative rounded-2xl bg-neutral-900/50 border border-white/5 hover:border-white/20 overflow-hidden card-hover w-[280px] md:w-auto flex-shrink-0 md:flex-shrink"
                  data-testid={`demo-card-${product.product_id}`}
                >
                  <div
                    className="aspect-video bg-neutral-800 overflow-hidden cursor-pointer relative"
                    onClick={() => { trackActivity(product.product_id, 'view'); setPreviewSlug(product.slug || product.product_id); }}
                  >
                    <ResilientImg src={getProductImage(product)} alt={product.name} className="w-full h-full object-cover" />
                    <div className="absolute inset-0 bg-black/30 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
                      <div className="bg-white text-black px-4 py-2 rounded-full text-sm font-bold flex items-center gap-2">
                        <Play className="w-4 h-4" />
                        Watch Preview
                      </div>
                    </div>
                    <div className="absolute top-3 left-3 bg-[#00F0FF]/90 text-black px-2.5 py-1 rounded-full text-xs font-bold flex items-center gap-1">
                      <Zap className="w-3 h-3 text-black" />
                      Live Preview
                    </div>
                  </div>
                  <div className="p-5">
                    <h3
                      className="font-bold text-lg mb-1.5 cursor-pointer hover:text-[#00F0FF] line-clamp-1"
                      onClick={() => { trackActivity(product.product_id, 'view'); navigate(`/product/${product.product_id}`); }}
                    >
                      {product.name}
                      {product.verified && <BadgeCheck className="w-4 h-4 text-[#00F0FF] inline ml-1.5 -mt-0.5" />}
                    </h3>
                    <p className="text-neutral-400 text-sm mb-3 line-clamp-2">{product.description}</p>
                    <div className="flex items-center gap-2 mb-3">
                      <div className="flex items-center">
                        <Star className="w-3.5 h-3.5 fill-[#00F0FF] text-[#00F0FF]" />
                        <span className="text-sm font-semibold ml-1">{product.rating}</span>
                      </div>
                      <span className="text-xs text-neutral-500">({product.total_reviews} reviews)</span>
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="text-xl font-bold text-[#00F0FF]">${product.price}</span>
                      <Button
                        onClick={() => { if (user) { addToCart(product); } else { toast.error(t('toast.signInToAddCart')); } }}
                        className="bg-white hover:bg-neutral-200 text-black rounded-full text-sm"
                        data-testid={`demo-add-${product.product_id}`}
                      >
                        {t('product.addToCart')}
                      </Button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Creator CTA Banner — landing-page-only (signed-in users
          already see their dashboard onboarding). */}
      {!user && (
      <section className="container mx-auto px-4 md:px-12 py-10 md:py-16 border-t border-white/10" data-testid="creator-cta-section">
        <div className="relative rounded-2xl overflow-hidden" style={{ background: 'linear-gradient(135deg, #1a0a2e 0%, #0a1628 60%, #0a0a0a 100%)' }}>
          <div className="absolute top-0 left-1/4 w-96 h-96 bg-white text-black/8 rounded-full blur-[120px] pointer-events-none" />
          <div className="absolute bottom-0 right-0 w-72 h-72 bg-[#00F0FF]/5 rounded-full blur-[100px] pointer-events-none" />
          <div className="relative z-10 p-8 md:p-12 lg:p-16 text-center">
            <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full border border-white/30 bg-white/5 mb-6">
              <Store className="w-3.5 h-3.5 text-white" />
              <span className="text-xs font-semibold tracking-wider uppercase text-white">For AI Creators</span>
            </div>
            <h2 className="text-3xl md:text-4xl lg:text-5xl font-black text-white mb-4" style={{ fontFamily: 'Outfit, sans-serif' }}>
              List Your AI Product.<br />
              <span className="text-white">Start Earning Today.</span>
            </h2>
            <p className="text-neutral-400 text-base md:text-lg max-w-xl mx-auto mb-8 leading-relaxed">
              Join a growing community of AI entrepreneurs. Built-in audience, powerful analytics, and only a 5% platform fee to grow your revenue.
            </p>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-6 md:gap-10 mb-8">
              <div className="flex items-center gap-2 text-sm text-neutral-300">
                <CheckCircle className="w-4 h-4 text-[#00F0FF] shrink-0" />
                <span>List in under 5 minutes</span>
              </div>
              <div className="flex items-center gap-2 text-sm text-neutral-300">
                <CheckCircle className="w-4 h-4 text-[#00F0FF] shrink-0" />
                <span>Only 5% platform fee</span>
              </div>
              <div className="flex items-center gap-2 text-sm text-neutral-300">
                <CheckCircle className="w-4 h-4 text-[#00F0FF] shrink-0" />
                <span>Built-in link analytics</span>
              </div>
              <div className="flex items-center gap-2 text-sm text-neutral-300">
                <CheckCircle className="w-4 h-4 text-[#00F0FF] shrink-0" />
                <span>Built-in Affiliate Program</span>
              </div>
            </div>
            {!user ? (
              <Button
                onClick={() => setShowAuthModal(true)}
                className="bg-white hover:bg-neutral-200 text-black rounded-full px-10 py-6 text-lg font-bold"
                data-testid="creator-cta-button"
              >
                Become a Creator
              </Button>
            ) : user.role !== 'creator' && user.role !== 'admin' ? (
              <Button
                onClick={() => navigate('/apply-creator')}
                className="bg-white hover:bg-neutral-200 text-black rounded-full px-10 py-6 text-lg font-bold"
                data-testid="creator-cta-button"
              >
                Apply to Sell
              </Button>
            ) : null}
          </div>
        </div>
      </section>
      )}


      {/* From the Blog - Content Flywheel */}
      {blogPosts.length > 0 && (
        <section className="container mx-auto px-4 md:px-12 py-8 md:py-12 border-t border-white/10" data-testid="blog-section">
          <div className="flex items-center justify-between mb-6 md:mb-8">
            <div>
              <div className="flex items-center gap-2 px-3 py-1.5 rounded-full border border-white/30 bg-white/5 mb-4 w-fit">
                <BookOpen className="w-4 h-4 text-white" />
                <span className="text-xs font-semibold tracking-wider uppercase text-white">From the Blog</span>
              </div>
              <h2 className="text-2xl md:text-3xl font-bold" style={{ fontFamily: 'Outfit, sans-serif' }}>
                Insights for AI Entrepreneurs
              </h2>
            </div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 md:gap-6">
            {blogPosts.map((post) => (
              <article
                key={post.post_id}
                onClick={() => navigate(`/resources?article=${post.post_id}`)}
                className="group rounded-2xl bg-neutral-900/50 border border-white/5 hover:border-white/20 overflow-hidden cursor-pointer transition-all duration-200"
                data-testid={`blog-card-${post.post_id}`}
              >
                <div className="aspect-[16/9] bg-neutral-800 overflow-hidden">
                  <img
                    src={post.image}
                    alt={post.title}
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                    onError={(e) => { e.target.src = 'https://images.unsplash.com/photo-1677442136019-21780ecad995?w=400&h=300&fit=crop'; }}
                  />
                </div>
                <div className="p-5">
                  <div className="flex items-center gap-2 mb-3">
                    <span className="text-xs font-semibold px-2 py-0.5 rounded-full bg-white/10 text-neutral-300">{post.category}</span>
                    <span className="text-xs text-neutral-500">{post.read_time}</span>
                  </div>
                  <h3 className="font-bold text-base mb-2 text-white group-hover:text-[#00F0FF] transition-colors line-clamp-2">
                    {post.title}
                  </h3>
                  <p className="text-neutral-400 text-sm line-clamp-2 mb-3">{post.excerpt}</p>
                  <span className="text-sm text-[#00F0FF] font-medium flex items-center gap-1 group-hover:gap-2 transition-all">
                    Read more <ArrowRight className="w-3.5 h-3.5" />
                  </span>
                </div>
              </article>
            ))}
          </div>
        </section>
      )}

      {/* FAQ — WhyIQ audit Phase 3 fix. Single source of truth lives in
          `<HomeFAQ />`; both homepages (Home.js and ComingSoon.js) render
          the same component so visible UI and FAQPage JSON-LD can never
          drift apart. Landing-page-only — signed-in users don't need
          marketing FAQs. */}
      {!user && (
        <HomeFAQ onFullSetClick={() => { navigate('/faqs') }} />
      )}

      {/* The AI Ledger Brief — newsletter signup. Sits between the FAQ and
          the footer so it picks up readers who scrolled all the way down
          (= highest-intent) without competing with the primary CTAs above.
          Opt-in only, separate unsubscribe from transactional mail. */}
      <section className="container mx-auto px-6 md:px-12 mt-12 mb-8 max-w-3xl" data-testid="home-newsletter-section">
        <NewsletterSignup source="homepage_footer" />
      </section>

      {/* Footer */}
      <footer className="border-t border-white/10 mt-20 py-12">
        <div className="container mx-auto px-6 md:px-12">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-8">
            {/* Brand */}
            <div className="md:col-span-1">
              <h3 className="text-2xl font-black mb-4">
                <span className="text-[#00F0FF]">theailedger.ai</span>
              </h3>
              <p className="text-neutral-400 text-sm">
                The premier marketplace for AI products and tools. Discover, buy, and sell cutting-edge AI solutions.
              </p>
            </div>

            {/* Quick Links */}
            <div>
              <h4 className="font-semibold mb-4 text-white">Quick Links</h4>
              <ul className="space-y-2 text-neutral-400 text-sm">
                <li><button onClick={() => navigate('/')} className="hover:text-[#00F0FF] transition-colors">Home</button></li>
                <li><button onClick={() => { navigate('/submit') }} className="text-[#00F0FF] font-semibold hover:text-[#00F0FF]/80 transition-colors" data-testid="footer-submit-link">Sell Your AI Tool</button></li>
                <li><button onClick={() => { navigate('/partners/platforms') }} className="hover:text-[#FFB400] transition-colors" data-testid="footer-platform-partner-link">For Platforms ($99/mo)</button></li>
                <li><button onClick={() => { navigate('/leaderboard') }} className="hover:text-[#00F0FF] transition-colors">Creator Leaderboard</button></li>
                <li><button onClick={() => navigate('/my-purchases')} className="hover:text-[#00F0FF] transition-colors">My Purchases</button></li>
                <li><button onClick={() => { navigate('/resources') }} className="hover:text-[#00F0FF] transition-colors">Resources</button></li>
                {(user?.role === 'creator' || user?.role === 'admin') && (
                  <li><button onClick={() => navigate('/dashboard')} className="hover:text-[#00F0FF] transition-colors">Seller Dashboard</button></li>
                )}
              </ul>
            </div>

            {/* Legal */}
            <div>
              <h4 className="font-semibold mb-4 text-white">Legal</h4>
              <ul className="space-y-2 text-neutral-400 text-sm">
                <li><button onClick={() => { navigate('/terms') }} className="hover:text-[#00F0FF] transition-colors">Terms & Conditions</button></li>
                <li><button onClick={() => { navigate('/privacy') }} className="hover:text-[#00F0FF] transition-colors">Privacy Policy</button></li>
                <li><button onClick={() => { navigate('/refund-policy') }} className="hover:text-[#00F0FF] transition-colors">Refund Policy</button></li>
                <li><button onClick={() => { navigate('/security') }} className="hover:text-[#00F0FF] transition-colors" data-testid="footer-security-link">Security &amp; Trust</button></li>
                <li><button onClick={() => { navigate('/legal/dmca') }} className="hover:text-[#00F0FF] transition-colors" data-testid="footer-dmca-link">DMCA Takedown</button></li>
                <li><button onClick={() => { navigate('/privacy/request-my-data') }} className="hover:text-[#00F0FF] transition-colors" data-testid="footer-dsar-link">Request my data</button></li>
                <li><button onClick={() => { navigate('/faqs') }} className="hover:text-[#00F0FF] transition-colors">FAQs</button></li>
              </ul>
            </div>

            {/* Support */}
            <div>
              <h4 className="font-semibold mb-4 text-white">Support</h4>
              <ul className="space-y-2 text-neutral-400 text-sm">
                <li><button onClick={() => { navigate('/about') }} className="hover:text-[#00F0FF] transition-colors">About Us</button></li>
                <li><button onClick={() => { navigate('/press') }} className="hover:text-[#00F0FF] transition-colors" data-testid="footer-press-link">Press</button></li>
                <li><button onClick={() => { navigate('/contact') }} className="hover:text-[#00F0FF] transition-colors">Contact Us</button></li>
                <li><a href="mailto:support@theailedger.ai" className="hover:text-[#00F0FF] transition-colors">support@theailedger.ai</a></li>
              </ul>
            </div>
          </div>

          {/* Bottom Bar */}
          <div className="border-t border-white/10 mt-8 pt-8 flex flex-col md:flex-row items-center justify-between gap-4">
            <p className="text-neutral-500 text-sm">
              © 2026 theailedger.ai. All rights reserved.
            </p>
            
            {/* Social Media Buttons */}
            <div className="flex items-center gap-4">
              <a 
                href="https://www.instagram.com/theailedger.ai" 
                target="_blank" 
                rel="noopener noreferrer"
                className="w-10 h-10 rounded-full bg-neutral-800 hover:bg-gradient-to-br hover:from-[#833AB4] hover:via-[#FD1D1D] hover:to-[#FCAF45] flex items-center justify-center transition-colors"
                aria-label="Instagram"
                data-testid="social-instagram"
              >
                <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z"/>
                </svg>
              </a>
              <a 
                href="https://www.tiktok.com/@theailedger.ai" 
                target="_blank" 
                rel="noopener noreferrer"
                className="w-10 h-10 rounded-full bg-neutral-800 hover:bg-black flex items-center justify-center transition-colors group"
                aria-label="TikTok"
                data-testid="social-tiktok"
              >
                <svg className="w-5 h-5 group-hover:text-white" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M19.59 6.69a4.83 4.83 0 0 1-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 0 1-5.2 1.74 2.89 2.89 0 0 1 2.31-4.64 2.93 2.93 0 0 1 .88.13V9.4a6.84 6.84 0 0 0-1-.05A6.33 6.33 0 0 0 5 20.1a6.34 6.34 0 0 0 10.86-4.43v-7a8.16 8.16 0 0 0 4.77 1.52v-3.4a4.85 4.85 0 0 1-1-.1z"/>
                </svg>
              </a>
            </div>
          </div>
        </div>
      </footer>

      <LivePreviewModal
        productSlug={previewSlug}
        onClose={() => setPreviewSlug(null)}
        onAddToCart={(p) => { if (user) addToCart(p); else toast.error(t('toast.signInToAddCart')); }}
      />
    </div>
  );
}

// Reusable mobile nav item
function MobileNavItem({ icon, label, badge, accent, onClick, testId }) {
  return (
    <button onClick={onClick} className="w-full flex items-center gap-3 px-3 py-2.5 rounded-lg hover:bg-white/5 transition-colors text-sm group" data-testid={testId}>
      <span className={accent ? `text-[${accent}]` : 'text-neutral-400 group-hover:text-white'}>{icon}</span>
      <span className="flex-1 text-left text-neutral-200">{label}</span>
      {badge > 0 && <span className="bg-red-500 text-white text-[10px] rounded-full min-w-[18px] h-[18px] flex items-center justify-center px-1">{badge > 9 ? '9+' : badge}</span>}
      <ChevronRight className="w-3.5 h-3.5 text-neutral-600" />
    </button>
  );
}

export default Home;
